import { telemetryIncrementAction } from 'core-events/actions';
import camelCase from 'lodash/camelCase';

import { AI_TOOLKIT_CATEGORIES, type AiToolkitCategory } from './types';

/**
 * Video Edit keeps the legacy `ai.contentEdit` prefix it had in the StockBlocks VideoEditorModal so
 * existing dashboards and Kibana queries stay continuous across the move into the toolkit.
 */
const TAG_PREFIXES: Record<AiToolkitCategory, string> = {
    [AI_TOOLKIT_CATEGORIES.VIDEO_EDIT]: 'ai.contentEdit',
    [AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION]: 'ai.videoGeneration',
    [AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION]: 'ai.imageGeneration',
    [AI_TOOLKIT_CATEGORIES.VOICEOVER]: 'ai.voiceover',
};

/**
 * StockBlocks emits through kafka-rest-client's `Telemetry.increment`, which normalizes the tag
 * before publishing. The shell's `TelemetryEventFactory` writes `event.tag` verbatim, so the same
 * normalization has to happen here or the two sources would write different tags for the same click.
 */
const normalizeTag = (tag: string): string =>
    tag
        .toLowerCase()
        .replace(/[\t\s\n]/g, '_')
        .replace(/["'`]/g, '');

export const AI_TOOLKIT_TELEMETRY_ACTIONS = {
    generateVideo: 'buttonClick.generateVideo',
    regenerateWithNewPrompt: 'buttonClick.regenerateWithNewPrompt',
    pricingRedirect: 'buttonClick.pricingRedirect',
    purchaseCredits: 'buttonClick.purchaseCredits',
} as const;

/**
 * Telemetry failures are logged rather than thrown, so a misspelled action would publish a tag that
 * silently never matches a dashboard. Constraining the type makes that a compile error instead.
 */
type AiToolkitTelemetryAction =
    | (typeof AI_TOOLKIT_TELEMETRY_ACTIONS)[keyof typeof AI_TOOLKIT_TELEMETRY_ACTIONS]
    | `suggestion.${string}`
    | `useCase.${string}`;

/**
 * Legacy camelCases dynamic segments with lodash. The toolkit's own `utils/camelCase` only handles
 * `[-_]`, so it would produce a different tag for suggestion labels, which contain spaces.
 */
export const toSuggestionAction = (label: string): `suggestion.${string}` => `suggestion.${camelCase(label)}`;

export const toUseCaseAction = (useCaseId: string): `useCase.${string}` => `useCase.${camelCase(useCaseId)}`;

export const emitAiToolkitTag = (category: AiToolkitCategory, action: AiToolkitTelemetryAction): void => {
    void telemetryIncrementAction(normalizeTag(`${TAG_PREFIXES[category]}.${action}`));
};
